/**
 * useCheckoutPayment — extracts wallet-connection + transaction logic
 * from VotingPayment into a hook that the new BuyGetCheckout can drive.
 */
import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ethers } from "ethers";
import axios from "axios";
import {
  useWeb3Modal,
  useWeb3ModalAccount,
  useWeb3ModalProvider,
  useWeb3ModalEvents,
  useDisconnect,
} from "@web3modal/ethers5/react";
import { useTonWallet } from "@tonconnect/ui-react";
import { collection, onSnapshot, query, where, limit } from "firebase/firestore";

import UserContext from "../../Contexts/User";
import { auth, db } from "../../firebase";
import { mainnet } from "../../Components/Profile/utils";
import { fetchPrices } from "../../common/models/utils";
import {
  createProviderWithFallback,
  executeWithRetry,
} from "../../common/utils/web3Utils";
import { logPaymentError } from "../../common/utils/logPaymentError";

/* ───────── Coin config objects (same as VotingPayment) ───────── */
const USDT_COINS = [
  {
    chainId: 1,
    name: "Ethereum",
    PayWith: "Ethereum",
    ContractAddress: "0xdAC17F958D2ee523a2206206994597C13D831ec7",
    currency: "ETH",
    Type: "USDT",
  },
  {
    chainId: 56,
    name: "Bsc",
    PayWith: "Binance",
    ContractAddress: "0x55d398326f99059ff775485246999027b3197955",
    currency: "BNB",
    Type: "USDT",
  },
  {
    chainId: 137,
    name: "Polygon",
    PayWith: "Polygon Mainnet",
    ContractAddress: "0xc2132D05D31c914a87C6611C10748AEb04B58e8F",
    currency: "MATIC",
    Type: "USDT",
  },
];

/**
 * Map the new checkout's simple coin label to the correct coinInfo object
 * that the existing backend / wallet flow expects.
 */
export const COIN_MAP: Record<string, any> = {
  USDT: USDT_COINS[0], // USDT on Ethereum by default
  ETH: mainnet.find((c) => c.currency === "ETH"),
  BNB: mainnet.find((c) => c.currency === "BNB"),
  TON: { chainId: "-239", name: "TON", currency: "TON" },
  SOL: null, // not yet supported
  More: null,
};

/* ───────── types ───────── */
export type PayStatus = { type: "" | "success" | "error"; message: string };

export interface CheckoutPaymentHook {
  /** current status — drive UI from this */
  status: PayStatus;
  /** true while waiting for wallet / tx / api */
  busy: boolean;
  /** wallet address (if connected) */
  walletAddress: string | undefined;
  /** connected chain id */
  walletChainId: number | undefined;
  /** is web3 wallet connected */
  isWalletConnected: boolean;
  /** call to initiate the checkout (connect wallet → switch chain → send tx) */
  pay: (coinKey: string, payamount: number, productId: any, qty: number) => void;
  /** call for credit-card fallback */
  payWithCard: (payamount: number, productId: any, qty: number) => void;
  /** reset status so user can retry */
  reset: () => void;
  /** post-payment data from backend */
  afterPaymentData: any[];
  /** for TON: the ton wallet object */
  tonWallet: any;
  /** navigate to purchase history */
  goToPurchases: () => void;
}

export default function useCheckoutPayment(): CheckoutPaymentHook {
  const { user, userInfo } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();

  const { open } = useWeb3Modal();
  useDisconnect(); // keep hook registered
  const { address, chainId, isConnected } = useWeb3ModalAccount();
  const { walletProvider } = useWeb3ModalProvider();
  const events = useWeb3ModalEvents();
  const tonWallet = useTonWallet();

  const [status, setStatus] = useState<PayStatus>({ type: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [afterPaymentData, setAfterPaymentData] = useState<any[]>([]);

  // stash the pending pay call so we can resume after wallet connects
  const [pendingPay, setPendingPay] = useState<{
    coinKey: string;
    payamount: number;
    productId: any;
    qty: number;
  } | null>(null);

  /* ─── credit-card callback listener (same as VotingPayment) ─── */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const userIdCurrent = params.get("userId");
    if (userIdCurrent && userInfo?.uid && userIdCurrent === userInfo.uid) {
      setBusy(true);
      const raw = localStorage.getItem(`${userInfo.uid}_IntentId`);
      if (raw) {
        const parsed = JSON.parse(raw);
        listenForCardPayment(parsed[1]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.uid]);

  const listenForCardPayment = (intentId: string) => {
    if (!userInfo?.uid || !intentId) return;
    const q = query(
      collection(db, "callbackHistory"),
      where("userId", "==", user?.uid || ""),
      limit(3)
    );
    onSnapshot(q, (snap) => {
      snap.forEach((doc) => {
        const d = doc.data();
        if (d?.userId === userInfo.uid && d?.intentId === intentId) {
          if (
            d.event === "ProcessingFiatProviderOrder" ||
            d.event === "BlockchainTransactionSubmission" ||
            d.event === "Completed"
          ) {
            setBusy(false);
            setStatus({ type: "success", message: "" });
          }
          if (d.event === "Failed") {
            setBusy(false);
            setStatus({ type: "error", message: "Payment failed, please try again" });
          }
        }
      });
    });
  };

  /* ─── resume after wallet connects ─── */
  useEffect(() => {
    if (events?.data?.event === "CONNECT_SUCCESS" && pendingPay) {
      const { coinKey, payamount, productId, qty } = pendingPay;
      setPendingPay(null);
      executePay(coinKey, payamount, productId, qty);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  /* ─── switch network helper ─── */
  const switchNetwork = async (targetChainId: number) => {
    const ethereum = (window as any).ethereum;
    const provider = new ethers.providers.Web3Provider(walletProvider || ethereum);
    try {
      return await provider.send("wallet_switchEthereumChain", [
        { chainId: `0x${targetChainId.toString(16)}` },
      ]);
    } catch (error: any) {
      logPaymentError(error, {
        source: "useCheckoutPayment.switchNetwork",
        flow: "wallet-switch",
        chainId: targetChainId,
        walletChainId: chainId,
        walletAddress: address,
        extra: { targetChainId },
      });
      if (error?.code === 4902) {
        setStatus({
          type: "error",
          message: `Please add the network in your wallet to pay on this chain`,
        });
      } else {
        setStatus({ type: "error", message: "User rejected the request" });
      }
      setBusy(false);
      throw error;
    }
  };

  /* ─── payNow — send result to backend ─── */
  const payNow = async (
    detail: any,
    coinInfo: any,
    payamount: number,
    productId: any,
    qty: number
  ) => {
    const liveAmount = JSON.parse(localStorage.getItem("CoinsPrice") || "{}");
    const payValue =
      coinInfo?.Type === "USDT"
        ? Number(payamount)
        : Number(payamount) / (liveAmount[coinInfo?.currency]?.price || 1);

    const data = {
      userId: user?.uid,
      userEmail: user?.email,
      walletType: "wallet connect",
      amount: payValue,
      dollarAmount: Number(payamount),
      VTEAmount: 0,
      network: `${coinInfo.chainId || ""}`,
      origincurrency: `${coinInfo?.currency?.toLowerCase()}`,
      token: coinInfo?.Type === "USDT"
        ? `USDT ${coinInfo?.name}`
        : coinInfo?.name,
      event: "Approved",
      transactionType: "PRODUCT_PURCHASE",
      numberOfVotes: 0,
      paymentDetails: detail,
      votePackageId: "",
      miningPassDurationInDays: 0,
      productId,
      quantity: qty,
    };

    const headers = {
      accept: "application/json",
      Authorization: `Bearer ${(auth?.currentUser as any)?.accessToken}`,
      "content-type": "application/json",
    };

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_V2E}updateUserAfterPayment`,
        { data },
        { headers }
      );
      setAfterPaymentData(res.data);
      setBusy(false);
      setStatus({ type: "success", message: "" });
    } catch (error: any) {
      logPaymentError(error, {
        source: "useCheckoutPayment.payNow",
        flow: "updateUserAfterPayment",
        coinInfo,
        payamount,
        productId,
        quantity: qty,
        chainId: coinInfo?.chainId,
        walletChainId: chainId,
        walletAddress: address,
        walletType: "wallet connect",
        transactionType: "PRODUCT_PURCHASE",
        extra: { paymentDetailsHash: detail?.hash, orderId: detail?.orderId },
      });
      setBusy(false);
      setStatus({
        type: "error",
        message: "Connection issue. Please try again.",
      });
    }
  };

  /* ─── sendTransaction ─── */
  const sendTransaction = async (
    coinInfo: any,
    payamount: number,
    productId: any,
    qty: number
  ) => {
    // For native transfers we must convert USD → crypto using a live price.
    // Await the price fetch so we don't race localStorage on iOS Safari (ITP
    // can evict localStorage on first visit / after 7d inactivity), and refuse
    // to send the tx if the price is missing — otherwise the silent fallback
    // would treat the dollar amount as a crypto amount and the wallet would
    // surface INSUFFICIENT_FUNDS for an unrelated reason.
    let amountInCrypto = "0";
    if (coinInfo?.Type !== "USDT") {
      await fetchPrices();
      const liveAmount = JSON.parse(localStorage.getItem("CoinsPrice") || "{}");
      const priceRaw = liveAmount?.[coinInfo?.currency]?.price;
      const price = Number(priceRaw);
      if (!priceRaw || priceRaw === "N/A" || !Number.isFinite(price) || price <= 0) {
        logPaymentError(new Error("Missing live crypto price"), {
          source: "useCheckoutPayment.sendTransaction",
          flow: "price-precheck",
          coinInfo,
          payamount,
          productId,
          quantity: qty,
          chainId: coinInfo?.chainId,
          walletChainId: chainId,
          walletAddress: address,
          walletType: "wallet connect",
          transactionType: "PRODUCT_PURCHASE",
          extra: { priceRaw: priceRaw ?? null, currency: coinInfo?.currency ?? null },
        });
        setStatus({
          type: "error",
          message: `Couldn't fetch live ${coinInfo?.currency} price. Please check your connection and try again.`,
        });
        setBusy(false);
        return;
      }
      amountInCrypto = Number(payamount / price).toFixed(18);
    }

    const timeout = setTimeout(() => {
      setBusy(false);
    }, 120000);

    try {
      const ethereum = (window as any).ethereum;
      const provider = await createProviderWithFallback(
        coinInfo?.chainId,
        walletProvider || ethereum
      );

      if (!(provider instanceof ethers.providers.Web3Provider)) {
        throw new Error("Web3Provider is required");
      }

      const wallet = provider.getSigner();
      await executeWithRetry(async () => {
        const response = await provider.send("eth_requestAccounts", []);
        if (response.length === 0) throw new Error("No accounts found");
        return response;
      });

      const toAddress =
        coinInfo?.chainId === 56
          ? process.env.REACT_APP_BNB_ACCOUNT_KEY
          : coinInfo?.chainId === 137
          ? process.env.REACT_APP_MATIC_ACCOUNT_KEY
          : process.env.REACT_APP_ETH_ACCOUNT_KEY;

      if (coinInfo?.Type === "USDT") {
        const USDT_ABI = [
          "function transfer(address to, uint amount) public returns (bool)",
          "function balanceOf(address account) external view returns (uint256)",
          "function decimals() external view returns (uint8)",
        ];
        const contract = new ethers.Contract(
          coinInfo.ContractAddress,
          USDT_ABI,
          wallet
        );
        const decimals = await contract.decimals();
        const amount = Number(payamount).toFixed(decimals);
        const amountInUnits = ethers.utils.parseUnits(amount.toString(), decimals);
        const balance = await contract.balanceOf(address);
        if (balance.lt(amountInUnits)) {
          // Log even though we don't throw — this branch was previously
          // invisible to triage and is the most common USDT failure mode.
          logPaymentError(new Error("Insufficient USDT balance"), {
            source: "useCheckoutPayment.sendTransaction",
            flow: "balance-check",
            coinInfo,
            payamount,
            amountInCrypto,
            productId,
            quantity: qty,
            chainId: coinInfo?.chainId,
            walletChainId: chainId,
            walletAddress: address,
            walletType: "wallet connect",
            transactionType: "PRODUCT_PURCHASE",
            extra: {
              balanceUnits: balance.toString(),
              requiredUnits: amountInUnits.toString(),
              decimals,
              usdtContract: coinInfo?.ContractAddress,
            },
          });
          setStatus({ type: "error", message: "Insufficient USDT balance" });
          setBusy(false);
          return;
        }
        const tx = await contract.transfer(toAddress, amountInUnits, {
          gasLimit: ethers.utils.hexlify(100000),
        });
        await tx.wait();
        if (tx.hash) {
          payNow(
            { ...tx, orderId: `VTE-PR-${tx.hash?.substring(0, 4)}` },
            coinInfo,
            payamount,
            productId,
            qty
          );
        }
      } else {
        const transaction = {
          to: toAddress,
          value: ethers.utils.parseUnits(amountInCrypto, "ether").toHexString(),
        };
        const txResponse = await wallet.sendTransaction(transaction);
        if (txResponse.hash) {
          payNow(
            {
              ...txResponse,
              orderId: `VTE-PR-${txResponse.hash?.substring(0, 4)}`,
            },
            coinInfo,
            payamount,
            productId,
            qty
          );
        }
      }
    } catch (error: any) {
      const debugRecipient = coinInfo?.chainId === 56
        ? process.env.REACT_APP_BNB_ACCOUNT_KEY
        : coinInfo?.chainId === 137
        ? process.env.REACT_APP_MATIC_ACCOUNT_KEY
        : process.env.REACT_APP_ETH_ACCOUNT_KEY;
      let debugValueWei: string | null = null;
      try {
        if (coinInfo?.Type !== "USDT") {
          debugValueWei = ethers.utils.parseUnits(amountInCrypto, "ether").toString();
        }
      } catch { /* noop */ }

      // Capture native balance + a fresh gas-price snapshot so we can see, in
      // the log, whether INSUFFICIENT_FUNDS is real (balance < value + gas) or
      // a misclassification. Best-effort; failures don't block the log.
      let nativeBalanceWei: string | null = null;
      let gasPriceWei: string | null = null;
      try {
        const ethereum = (window as any).ethereum;
        const probe = walletProvider
          ? new ethers.providers.Web3Provider(walletProvider)
          : ethereum
          ? new ethers.providers.Web3Provider(ethereum)
          : await createProviderWithFallback(coinInfo?.chainId);
        if (address) {
          const bal = await probe.getBalance(address);
          nativeBalanceWei = bal.toString();
        }
        const gp = await probe.getGasPrice();
        gasPriceWei = gp.toString();
      } catch { /* noop */ }

      logPaymentError(error, {
        source: "useCheckoutPayment.sendTransaction",
        flow: "wallet-tx",
        coinInfo,
        payamount,
        amountInCrypto,
        productId,
        quantity: qty,
        chainId: coinInfo?.chainId,
        walletChainId: chainId,
        walletAddress: address,
        walletType: "wallet connect",
        transactionType: "PRODUCT_PURCHASE",
        extra: {
          recipient: debugRecipient,
          usdtContract: coinInfo?.Type === "USDT" ? coinInfo?.ContractAddress : null,
          valueWei: debugValueWei,
          nativeBalanceWei,
          gasPriceWei,
          isMobile: typeof navigator !== "undefined" ? /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) : null,
        },
      });
      const msg = (error?.message || "").toLowerCase();
      setBusy(false);
      if (error.code === -32603) {
        setStatus({
          type: "error",
          message: "Insufficient funds. Please top up to continue.",
        });
      } else if (error.code === "INSUFFICIENT_FUNDS" || msg.includes("insufficient funds")) {
        setStatus({
          type: "error",
          message: `Insufficient funds for ${coinInfo?.Type === "USDT" ? "USDT" : coinInfo?.currency}. Please top up.`,
        });
      } else if (msg.includes("user rejected")) {
        setStatus({ type: "error", message: "Transaction rejected by user" });
      } else {
        setStatus({
          type: "error",
          message: "Error sending transaction. Please try again.",
        });
      }
    } finally {
      clearTimeout(timeout);
    }
  };

  /* ─── executePay — the orchestrator ─── */
  const executePay = async (
    coinKey: string,
    payamount: number,
    productId: any,
    qty: number
  ) => {
    // For USDT, auto-pick the chain the wallet is currently on (1/56/137).
    // The default COIN_MAP entry is Ethereum-only, which falsely reported
    // "Insufficient USDT balance" when the user held USDT on BSC/Polygon.
    const coinInfo =
      coinKey === "USDT"
        ? USDT_COINS.find((c) => c.chainId === chainId) ?? COIN_MAP.USDT
        : COIN_MAP[coinKey];
    if (!coinInfo) {
      setStatus({ type: "error", message: `${coinKey} is not supported yet.` });
      setBusy(false);
      return;
    }

    // TON uses a separate flow — handled by the TONWallet component
    if (coinKey === "TON") {
      // We set the coin info in localStorage so TONWallet can pick it up
      localStorage.setItem("CoinPay", "TON");
      setStatus({ type: "error", message: "TON payments use the TON wallet interface. Please select a different coin for now." });
      setBusy(false);
      return;
    }

    localStorage.setItem("CoinPay", coinInfo.PayWith || coinInfo.name);

    if (!isConnected) {
      // Open Web3Modal — the CONNECT_SUCCESS event will resume
      setPendingPay({ coinKey, payamount, productId, qty });
      open();
      return;
    }

    setBusy(true);
    setStatus({ type: "", message: "" });

    try {
      if (coinInfo.chainId !== chainId) {
        await switchNetwork(coinInfo.chainId);
      }
      await sendTransaction(coinInfo, payamount, productId, qty);
    } catch {
      // switchNetwork already set the error status
    }
  };

  /* ─── public pay() ─── */
  const pay = useCallback(
    (coinKey: string, payamount: number, productId: any, qty: number) => {
      executePay(coinKey, payamount, productId, qty);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isConnected, chainId, walletProvider, address]
  );

  /* ─── credit card fallback ─── */
  const payWithCard = useCallback(
    (payamount: number, productId: any, qty: number) => {
      setBusy(true);
      const data = {
        userId: userInfo?.uid,
        email: userInfo?.email,
        amount: `${payamount * 1000000}`,
        transactionType: "PRODUCT_PURCHASE",
        VTEAmount: 0,
        numberOfVotes: 0,
        timestamp: new Date().getTime().toString(),
        chainId: "137",
        to: "0x9ad77D3185b1d0d37B6e0dc689872324910491D8",
        contractAddress: "0xc2132D05D31c914a87C6611C10748AEb04B58e8F",
        origincurrency: "matic",
        token: "matic",
        votePackageId: "",
        miningPassDurationInDays: 0,
        intentLimit: 0,
        productId,
        quantity: qty,
      };

      axios
        .post(
          `${process.env.REACT_APP_V2E}createPaymentThroughCreditCard`,
          { data },
          { headers: { accept: "application/json" } }
        )
        .then((res) => {
          window.open(res.data?.result?.redirectUrl, "_self");
          const match = res.data?.result?.redirectUrl?.match(/pay\/(.*)/)?.[1];
          if (match) {
            localStorage.setItem(
              `${userInfo?.uid}_IntentId`,
              JSON.stringify([userInfo?.uid, match])
            );
          }
        })
        .catch((error: any) => {
          logPaymentError(error, {
            source: "useCheckoutPayment.payWithCard",
            flow: "createPaymentThroughCreditCard",
            payamount,
            productId,
            quantity: qty,
            chainId: "137",
            walletType: "credit-card",
            transactionType: "PRODUCT_PURCHASE",
          });
          setBusy(false);
          setStatus({
            type: "error",
            message: "Connection issue. Please try again.",
          });
        });
    },
    [userInfo]
  );

  const reset = useCallback(() => {
    setStatus({ type: "", message: "" });
    setBusy(false);
  }, []);

  const goToPurchases = useCallback(() => {
    navigate("/profile/Purchases");
    localStorage.removeItem("PayAmount");
  }, [navigate]);

  return {
    status,
    busy,
    walletAddress: address,
    walletChainId: chainId,
    isWalletConnected: isConnected,
    pay,
    payWithCard,
    reset,
    afterPaymentData,
    tonWallet,
    goToPurchases,
  };
}
